import { Link, useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Check, CheckCheck, ExternalLink, Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { resolveNavigationTarget } from "@/lib/notificationRouting";
import type { AdminNotification } from "@/hooks/useAdminNotifications";

interface AdminNotificationPanelProps {
  notifications: AdminNotification[];
  isLoading: boolean;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

const categoryIcons: Record<string, string> = {
  user: "👤",
  business: "🏢",
  payment: "💳",
  communication: "💬",
  engagement: "⭐",
  system: "⚙️",
  content: "📝",
  leads: "🎯",
  affiliate: "🤝",
  booking: "📅",
};

const priorityStyles: Record<string, string> = {
  critical: "border-l-4 border-l-destructive bg-destructive/5",
  important: "border-l-4 border-l-orange-500 bg-orange-500/5",
  standard: "border-l-4 border-l-primary bg-primary/5",
  info: "border-l-4 border-l-muted-foreground/30",
};

export function AdminNotificationPanel({
  notifications,
  isLoading,
  onMarkAsRead,
  onMarkAllAsRead,
}: AdminNotificationPanelProps) {
  const navigate = useNavigate();
  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const handleClick = async (n: AdminNotification) => {
    if (!n.read_at) onMarkAsRead(n.id);
    const target = await resolveNavigationTarget(n.related_table, n.related_id);
    if (target) navigate(target);
    else if (n.related_table || n.related_id) toast.error("No se pudo encontrar el recurso relacionado");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          <span className="font-semibold text-sm">Notificaciones</span>
          {unreadCount > 0 && (
            <span className="text-xs bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={onMarkAllAsRead}
          >
            <CheckCheck className="h-3 w-3" />
            Marcar todo
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <ScrollArea className="h-[350px]">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
            <Bell className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">Sin notificaciones</p>
          </div>
        ) : (
          <div className="divide-y">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer",
                  !notification.read_at && "bg-muted/30",
                  priorityStyles[notification.priority] || priorityStyles.info
                )}
                onClick={() => handleClick(notification)}
              >
                <div className="flex items-start gap-3">
                  <span className="text-lg flex-shrink-0">
                    {categoryIcons[notification.event_category] || "📢"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className={cn(
                        "text-sm line-clamp-2",
                        !notification.read_at && "font-medium"
                      )}
                    >
                      {notification.title}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground capitalize">
                        {notification.event_category}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(notification.created_at), {
                          addSuffix: true,
                          locale: es,
                        })}
                      </span>
                    </div>
                  </div>
                  {!notification.read_at && (
                    <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Footer */}
      <Separator />
      <div className="p-2">
        <Link to="/admin/notifications">
          <Button variant="ghost" size="sm" className="w-full gap-2 text-xs">
            Ver todas las notificaciones
            <ExternalLink className="h-3 w-3" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
